import {ChevronRightIcon, GearIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, AnchoredOverlay, IconButton, SegmentedControl, VisuallyHidden} from '@primer/react'
import type React from 'react'
import {type ReactNode, useId, useRef, useState} from 'react'

import {findField, type FieldOption} from './field-option'
import styles from './ViewOptionsMenu.module.css'

export interface ViewOptionsMenuProps {
  /** Accessible name for the gear trigger button (e.g. "View options"). */
  triggerLabel: string
  /** Dialog heading — visually hidden but announced to screen readers and used as the dialog's accessible name. */
  title: string
  /**
   * The consumer-composed dialog content: an ordered set of `<ViewOptionsMenu.Layout>`,
   * `<ViewOptionsMenu.Group>`, and `<ViewOptionsMenu.ToggleGroup>` sections.
   */
  children: ReactNode
}

/**
 * A gear-icon trigger that opens an anchored dialog of view settings. The panel uses
 * `role="dialog"` semantics — controls are reached with `Tab`, not the arrow-key roving
 * focus of a menu — and changes apply immediately rather than on submit. The shell does not
 * inspect its children: a surface makes the structure explicit by composing
 * `<ViewOptionsMenu.Layout>`, `<ViewOptionsMenu.Group>`, and `<ViewOptionsMenu.ToggleGroup>`.
 *
 * The overlay is mounted only while open (and the gear is an external `anchorRef` rather than
 * `AnchoredOverlay`'s `renderAnchor`, since a `renderAnchor` anchor must stay mounted). Mounting
 * fresh on each open makes every open a "first open", which is what reliably moves focus into the
 * dialog — `AnchoredOverlay`'s focus trap only seizes focus on first mount, so a persistently
 * mounted overlay leaves focus on the trigger on the second and later opens.
 *
 * The overlay is intentionally non-modal (no `aria-modal`): the section `SingleSelect`s open as
 * flyout menus that need to escape this panel's clipping bounds, so they portal to the global
 * root and render as DOM siblings. It still traps keyboard focus while open — that is
 * `AnchoredOverlay`'s own behavior, independent of `aria-modal`.
 *
 * CSS anchor positioning is disabled (`cssAnchorPositioningSettings`) because with it enabled
 * `AnchoredOverlay` leaves `position` undefined, which never engages its focus trap — focus
 * would stay on the gear on open. Falling back to JS positioning keeps focus entry reliable in
 * production, where the `primer_react_css_anchor_positioning` flag is on.
 */
export function ViewOptionsMenu({triggerLabel, title, children}: ViewOptionsMenuProps) {
  const [open, setOpen] = useState(false)
  const titleId = useId()
  const anchorRef = useRef<HTMLButtonElement>(null)

  return (
    <>
      <IconButton
        ref={anchorRef}
        icon={GearIcon}
        aria-label={triggerLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(previous => !previous)}
      />
      {open && (
        <AnchoredOverlay
          open
          anchorRef={anchorRef}
          renderAnchor={null}
          onClose={() => setOpen(false)}
          align="end"
          width="medium"
          variant={{regular: 'anchored', narrow: 'fullscreen'}}
          className={styles.overlay}
          displayCloseButton
          closeButtonProps={{'aria-label': 'Close view options'}}
          focusZoneSettings={{disabled: true}}
          cssAnchorPositioningSettings={{disable: true}}
          overlayProps={{role: 'dialog', 'aria-labelledby': titleId}}
        >
          <VisuallyHidden>
            <h2 id={titleId}>{title}</h2>
          </VisuallyHidden>
          <div className={styles.content}>{children}</div>
        </AnchoredOverlay>
      )}
    </>
  )
}

interface SectionProps {
  /** Visible section heading, rendered as an `h3` and used to name the section's control. */
  label: string
  /** The id assigned to the heading, for associating the section's control. */
  labelId: string
  /** Optional trailing content in the header, e.g. a status badge. */
  trailing?: ReactNode
  children: ReactNode
}

/**
 * A labelled dialog section: a consistently styled `h3` heading and a body, drawn with a
 * divider between adjacent sections. It is a plain container, not a `region` landmark — the
 * dialog's heading hierarchy carries the structure, and each control names itself from the
 * heading, so an extra landmark would only add navigation noise.
 */
function Section({label, labelId, trailing, children}: SectionProps) {
  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h3 id={labelId} className={styles.sectionLabel}>
          {label}
        </h3>
        {trailing}
      </div>
      <div className={styles.sectionBody}>{children}</div>
    </div>
  )
}

export interface LayoutOption<TValue extends string = string> {
  value: TValue
  label: string
  icon?: React.ComponentType<{size?: number}>
}

export interface ViewOptionsLayoutProps<TValue extends string = string> {
  /** Section heading. */
  label: string
  /** Optional trailing content in the header, e.g. a status badge. */
  trailing?: ReactNode
  /** The selectable layouts, in display order. */
  options: ReadonlyArray<LayoutOption<TValue>>
  /** The currently selected layout. */
  value: TValue
  onSelect: (value: TValue) => void
}

/**
 * A labelled segmented control for switching layouts. The component is agnostic of what the
 * layouts are — a surface supplies its own set (list/board, table/board/roadmap, …) and maps
 * the reported value onto its own state.
 */
function Layout<TValue extends string = string>({
  label,
  trailing,
  options,
  value,
  onSelect,
}: ViewOptionsLayoutProps<TValue>) {
  const labelId = useId()

  return (
    <Section label={label} labelId={labelId} trailing={trailing}>
      <SegmentedControl
        aria-labelledby={labelId}
        size="small"
        fullWidth
        onChange={index => {
          const option = options[index]
          if (option) onSelect(option.value)
        }}
      >
        {options.map(option => {
          const Icon = option.icon
          return (
            <SegmentedControl.Button
              key={option.value}
              selected={option.value === value}
              leadingVisual={Icon ? () => <Icon /> : undefined}
            >
              {option.label}
            </SegmentedControl.Button>
          )
        })}
      </SegmentedControl>
    </Section>
  )
}

export interface ViewOptionsGroupProps {
  /** Visible group heading, also the accessible name of the list. */
  label: string
  /** Loading or error feedback, outside the list of controls. */
  status?: ReactNode
  /** One or more `<ViewOptionsMenu.SingleSelect>` rows. */
  children: ReactNode
}

/**
 * A labelled section wrapping single-select rows. Must contain
 * `<ViewOptionsMenu.SingleSelect>` rows.
 */
function Group({label, status, children}: ViewOptionsGroupProps) {
  const labelId = useId()

  return (
    <Section label={label} labelId={labelId}>
      {status}
      {!!children && (
        <ActionList aria-labelledby={labelId} className={styles.list}>
          {children}
        </ActionList>
      )}
    </Section>
  )
}

export interface ViewOptionsSingleSelectProps {
  /** The row's leading icon. */
  icon: React.ComponentType<{size?: number}>
  /** The row's visible label, e.g. "Group by". Also names the submenu. */
  label: string
  /** Shown as the current value, and as the submenu prompt, when nothing is selected. */
  placeholder: string
  /** The selectable fields, in display order. */
  fields: readonly FieldOption[]
  /** Identifier of the selected field, or `null` when nothing is chosen. */
  value: string | null
  onSelect: (identifier: string) => void
  disabled?: boolean
}

/**
 * A single-select button that opens a menu of fields — the shape "Group by", "Swimlane by",
 * and "Sort by" all share. The button shows the current selection beside a chevron, and the
 * menu marks the selected field. When `value` is `null` the button shows `placeholder` and
 * the menu marks nothing, prompting a choice rather than inventing a default. Draws nothing
 * when handed no fields. Must be rendered inside a `<ViewOptionsMenu.Group>`.
 */
function SingleSelect({
  icon: Icon,
  label,
  placeholder,
  fields,
  value,
  onSelect,
  disabled,
}: ViewOptionsSingleSelectProps) {
  if (fields.length === 0) return null

  const selected = findField(fields, value)
  const currentLabel = selected?.label ?? placeholder

  const trigger = (
    <ActionList.Item disabled={disabled}>
      <ActionList.LeadingVisual>
        <Icon />
      </ActionList.LeadingVisual>
      {label}
      <ActionList.TrailingVisual>
        <span className={styles.trailingValue}>
          {currentLabel}
          <ChevronRightIcon />
        </span>
      </ActionList.TrailingVisual>
    </ActionList.Item>
  )
  if (disabled) return trigger

  return (
    <ActionMenu>
      <ActionMenu.Anchor>{trigger}</ActionMenu.Anchor>
      <ActionMenu.Overlay width="medium" side="outside-left" align="start">
        <ActionList selectionVariant="single" aria-label={label}>
          {fields.map(field => {
            const FieldIcon = field.icon
            return (
              <ActionList.Item
                key={field.identifier}
                selected={field.identifier === value}
                onSelect={() => onSelect(field.identifier)}
              >
                <ActionList.LeadingVisual>
                  <FieldIcon />
                </ActionList.LeadingVisual>
                {field.label}
              </ActionList.Item>
            )
          })}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

export interface ViewOptionsToggleGroupProps {
  /** Visible group heading, also the accessible name of the listbox. */
  label: string
  /** The toggleable fields, in display order. */
  fields: readonly FieldOption[]
  /** Identifiers of the fields currently on. */
  selectedIds: ReadonlySet<string>
  onToggle: (identifier: string, next: boolean) => void
}

/**
 * A self-contained labelled group of toggleable fields, one per field, reporting whether each
 * is on. The rows are `option`s in a multi-select `listbox`, so Up/Down move between them and
 * Space or Enter toggles the focused one; the change announces through `aria-selected`.
 * Toggling a row leaves the dialog open so several fields can be flipped in one pass.
 */
function ToggleGroup({label, fields, selectedIds, onToggle}: ViewOptionsToggleGroupProps) {
  const labelId = useId()

  return (
    <Section label={label} labelId={labelId}>
      <ActionList
        role="listbox"
        selectionVariant="multiple"
        aria-labelledby={labelId}
        aria-multiselectable="true"
        className={styles.list}
      >
        {fields.map(field => {
          const Icon = field.icon
          const selected = selectedIds.has(field.identifier)
          return (
            <ActionList.Item
              key={field.identifier}
              selected={selected}
              onSelect={() => onToggle(field.identifier, !selected)}
            >
              <ActionList.LeadingVisual>
                <Icon />
              </ActionList.LeadingVisual>
              {field.label}
            </ActionList.Item>
          )
        })}
      </ActionList>
    </Section>
  )
}

ViewOptionsMenu.Layout = Layout
ViewOptionsMenu.Group = Group
ViewOptionsMenu.SingleSelect = SingleSelect
ViewOptionsMenu.ToggleGroup = ToggleGroup
